/**
 * Marple AI - Enhanced API Service
 * 
 * Features:
 * - Identity selector support
 * - Session management
 * - Skin analysis report handling
 * - Streaming chat
 * - LocalStorage persistence
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// ============================================
// TYPES
// ============================================
export interface Identity {
  id: 'leroy' | 'olufemi' | 'tshiamo';
  name: string;
  title: string;
  description: string;
  color: string;
  icon: string;
}

export interface ChatContext {
  page?: string;
  productId?: string;
  lastAnalysis?: string;
  report?: SkinAnalysisReport;
  userId?: string;
  isAuthenticated?: boolean;
  identity?: 'leroy' | 'olufemi' | 'tshiamo';
  cartItems?: Array<{ id: string; name: string; price: number; quantity: number }>;
  browseHistory?: string[];
  shoppingQuery?: string;
}

export interface SkinAnalysisReport {
  skinType?: string;
  concerns?: string[];
  hydration?: string;
  sensitivity?: string;
  pigmentation?: string;
  wrinkles?: string;
  acne?: string;
  pores?: string;
  overallScore?: number;
  recommendations?: string[];
  raw?: string;
}

export interface SessionData {
  userId: string;
  identity: 'leroy' | 'olufemi' | 'tshiamo';
  profile: Record<string, string>;
  hasHistory: boolean;
  messageCount: number;
}

export interface StreamChunk {
  content?: string;
  error?: string;
}

// ============================================
// IDENTITY MANAGEMENT
// ============================================

const IDENTITY_STORAGE_KEY = 'marple_identity';

export function getStoredIdentity(): 'leroy' | 'olufemi' | 'tshiamo' {
  try {
    const stored = localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (stored && ['leroy', 'olufemi', 'tshiamo'].includes(stored)) {
      return stored as 'leroy' | 'olufemi' | 'tshiamo';
    }
  } catch {
    // LocalStorage not available
  }
  return 'tshiamo';
}

export function setStoredIdentity(identity: 'leroy' | 'olufemi' | 'tshiamo'): void {
  try {
    localStorage.setItem(IDENTITY_STORAGE_KEY, identity);
  } catch {
    // LocalStorage not available
  }
}

const DEFAULT_IDENTITIES: Identity[] = [
  {
    id: 'leroy',
    name: 'Leroy',
    title: 'International Skincare Specialist',
    description: 'Warm, refined, and elegant with global expertise',
    color: '#BDD630',
    icon: 'Globe'
  },
  {
    id: 'olufemi',
    name: 'Olufemi',
    title: 'West African Skincare Specialist',
    description: 'Soulful, wise, and deeply knowledgeable about melanin-rich skin',
    color: '#F4C95D',
    icon: 'Sun'
  },
  {
    id: 'tshiamo',
    name: 'Tshiamo',
    title: 'Southern African Skincare Specialist',
    description: 'Grounded, practical, and focused on sun protection',
    color: '#8B9D83',
    icon: 'Shield'
  }
];

const IDENTITY_CACHE_KEY = 'marple_identities_cache';

export async function getIdentities(): Promise<Identity[]> {
  try {
    // Return cached identities immediately if available
    const cached = localStorage.getItem(IDENTITY_CACHE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as Identity[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Refresh in the background, but don't block or throw
          fetch(`${API_BASE_URL}/v1/identities`)
            .then(async (response) => {
              if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data.identities) && data.identities.length > 0) {
                  localStorage.setItem(IDENTITY_CACHE_KEY, JSON.stringify(data.identities));
                }
              }
            })
            .catch(() => {});
          return parsed;
        }
      } catch {
        // ignore parse errors
      }
    }

    const response = await fetch(`${API_BASE_URL}/v1/identities`);
    if (!response.ok) throw new Error('Failed to fetch identities');
    const data = await response.json();
    const identities = Array.isArray(data.identities) && data.identities.length > 0
      ? data.identities
      : DEFAULT_IDENTITIES;
    localStorage.setItem(IDENTITY_CACHE_KEY, JSON.stringify(identities));
    return identities;
  } catch (error) {
    // Endpoint is optional; fall back to cached or defaults silently.
    if (import.meta.env.DEV) {
      console.warn('[API] Identities endpoint unavailable, using fallback:', error);
    }
    const cached = localStorage.getItem(IDENTITY_CACHE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as Identity[];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch {
        // ignore
      }
    }
    return DEFAULT_IDENTITIES;
  }
}

// ============================================
// SESSION MANAGEMENT
// ============================================

export async function getSession(userId?: string): Promise<SessionData | null> {
  try {
    const url = userId 
      ? `${API_BASE_URL}/v1/session?userId=${encodeURIComponent(userId)}`
      : `${API_BASE_URL}/v1/session`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch session');
    return await response.json();
  } catch (error) {
    console.error('[API] Error fetching session:', error);
    return null;
  }
}

export async function updateSession(
  userId: string, 
  data: { identity?: string; profile?: Record<string, string> }
): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, ...data })
    });
    return response.ok;
  } catch (error) {
    console.error('[API] Error updating session:', error);
    return false;
  }
}

// ============================================
// CHAT API
// ============================================

export async function* streamChat(
  message: string,
  context: ChatContext = {},
  threadId?: string,
  signal?: AbortSignal
): AsyncGenerator<StreamChunk, void, unknown> {
  const response = await fetch(`${API_BASE_URL}/v1/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message,
      thread_id: threadId,
      context
    }),
    signal
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('data: ')) {
          const data = trimmed.slice(6);
          if (data === '[DONE]') return;

          try {
            const parsed = JSON.parse(data);
            yield parsed;
          } catch {
            // Skip invalid JSON
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ============================================
// REPORT UPLOAD
// ============================================

export async function uploadSkinReport(
  userId: string,
  report: SkinAnalysisReport
): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, report })
    });
    
    if (!response.ok) throw new Error('Failed to upload report');
    return await response.json();
  } catch (error) {
    console.error('[API] Error uploading report:', error);
    return { success: false, message: 'Failed to upload report' };
  }
}

// ============================================
// HEALTH CHECK
// ============================================

export async function checkHealth(): Promise<{
  status: string;
  kimi?: string;
  ai?: string;
  timestamp?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return await response.json();
  } catch (error) {
    return { status: 'error', ai: 'unknown' };
  }
}

// ============================================
// FALLBACK RESPONSES (when backend unavailable)
// ============================================

export function getFallbackWelcome(identity: string): string {
  const greetings: Record<string, string> = {
    leroy: "Hello, I'm Leroy, Marple & Company's skincare companion—created in loving memory and built to care with warmth, insight, and honesty. Tell me what's happening with your skin, and let's work through it together.",
    olufemi: "Hello, I'm Olufemi, Marple & Company's skincare companion—created in loving memory and built to care with warmth, insight, and honesty. Tell me what's happening with your skin, and let's work through it together.",
    tshiamo: "Hello, I'm Tshiamo, Marple & Company's skincare companion—created in loving memory and built to care with warmth, insight, and honesty. Tell me what's happening with your skin, and let's work through it together."
  };
  return greetings[identity] || greetings.leroy;
}

export function getFallbackResponse(message: string, identity: string): string {
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('dry') || lowerMessage.includes('hydrat')) {
    return "Dry skin needs gentle care. I'd recommend a hydrating cleanser, hyaluronic acid serum, and a rich moisturizer. Would you like me to suggest specific products from our range?";
  }
  
  if (lowerMessage.includes('oil') || lowerMessage.includes('acne') || lowerMessage.includes('pimple')) {
    return "For oily or acne-prone skin, consistency is key. A gentle foaming cleanser, niacinamide serum, and lightweight gel moisturizer can help balance your skin. Shall we discuss a full routine?";
  }
  
  if (lowerMessage.includes('sensitive') || lowerMessage.includes('red') || lowerMessage.includes('irritat')) {
    return "Sensitive skin requires extra care. Look for fragrance-free, hypoallergenic products with soothing ingredients like centella asiatica or chamomile. I'd be happy to recommend gentle options.";
  }
  
  if (lowerMessage.includes('anti-aging') || lowerMessage.includes('wrinkle') || lowerMessage.includes('fine line')) {
    return "For anti-aging concerns, I recommend starting with a good SPF daily, plus retinol at night and vitamin C in the morning. These are proven ingredients that work over time.";
  }
  
  const identityName = identity.charAt(0).toUpperCase() + identity.slice(1);
  return `I understand. As ${identityName}, I'm here to help you with personalized skincare guidance. Could you tell me more about your skin type and main concerns? This will help me give you the most relevant advice.`;
}

// ============================================
// REPORT PARSER
// ============================================

export function parseSkinAnalysisReport(rawText: string): SkinAnalysisReport {
  const report: SkinAnalysisReport = { raw: rawText };
  
  // Extract skin type
  const skinTypeMatch = rawText.match(/skin type[\s:]+(\w+)/i);
  if (skinTypeMatch) report.skinType = skinTypeMatch[1];
  
  // Extract concerns
  const concernsMatch = rawText.match(/concerns?[\s:]+([^\n]+)/i);
  if (concernsMatch) {
    report.concerns = concernsMatch[1].split(/[,;]/).map(c => c.trim());
  }
  
  // Extract hydration level
  const hydrationMatch = rawText.match(/hydration[\s:]+(\w+)/i);
  if (hydrationMatch) report.hydration = hydrationMatch[1];
  
  // Extract sensitivity
  const sensitivityMatch = rawText.match(/sensitivity[\s:]+(\w+)/i);
  if (sensitivityMatch) report.sensitivity = sensitivityMatch[1];
  
  // Extract overall score if present
  const scoreMatch = rawText.match(/score[\s:]+(\d+)/i) || rawText.match(/(\d+)[\s/]*100/);
  if (scoreMatch) report.overallScore = parseInt(scoreMatch[1]);
  
  return report;
}

// ============================================
// VISION ANALYSIS (Legacy Support)
// ============================================

export interface SkinReport {
  skinType: string;
  confidence: number;
  overallScore: number;
  findings: Array<{
    category: string;
    severity: 'low' | 'moderate' | 'high';
    description: string;
  }>;
  regimen: Array<{
    step: number;
    category: 'cleanse' | 'treat' | 'moisturize' | 'protect';
    timeOfDay: 'morning' | 'evening' | 'both';
    description: string;
  }>;
  recommendations: string[];
  ingredients: string[];
  avoidIngredients: string[];
}

export async function analyzeWithVision(photos: { front?: string; left?: string; right?: string }): Promise<{
  success: boolean;
  analysis?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/vision/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photos })
    });
    
    if (!response.ok) throw new Error('Vision analysis failed');
    return await response.json();
  } catch (error) {
    console.error('[Vision] Error:', error);
    return { 
      success: false, 
      error: 'Vision analysis unavailable',
      analysis: 'Based on your questionnaire, here is your personalized skin analysis...'
    };
  }
}

export async function analyzeSkin(data: {
  profile: Record<string, unknown>;
  questionnaire: Record<string, unknown>;
  photos?: { front?: string; left?: string; right?: string };
}): Promise<{ success: boolean; report?: SkinReport; error?: string }> {
  try {
    // First try vision analysis if photos provided
    let visionAnalysis = '';
    if (data.photos?.front) {
      const vision = await analyzeWithVision(data.photos);
      if (vision.analysis) {
        visionAnalysis = vision.analysis;
      }
    }
    
    // Generate report based on questionnaire + vision
    const report = generateMockReport(data, visionAnalysis);
    
    return { success: true, report };
  } catch (error) {
    console.error('[Analysis] Error:', error);
    return { 
      success: false, 
      error: 'Analysis failed. Please try again.',
      report: generateMockReport(data, '')
    };
  }
}

// Mock report generator for fallback
function generateMockReport(
  data: { profile: Record<string, unknown>; questionnaire: Record<string, unknown> },
  visionAnalysis: string
): SkinReport {
  const skinType = (data.questionnaire?.skinType as string) || 'combination';
  const concerns = (data.questionnaire?.concerns as string[]) || ['hydration'];
  
  return {
    skinType: skinType.charAt(0).toUpperCase() + skinType.slice(1),
    confidence: 85,
    overallScore: 72,
    findings: concerns.map((concern, i) => ({
      category: concern,
      severity: i === 0 ? 'moderate' : 'low' as 'low' | 'moderate' | 'high',
      description: `${concern} observed in the analysis.`
    })),
    regimen: [
      { step: 1, category: 'cleanse', timeOfDay: 'both', description: 'Gentle cleanser suitable for your skin type' },
      { step: 2, category: 'treat', timeOfDay: 'evening', description: 'Targeted serum for your primary concerns' },
      { step: 3, category: 'moisturize', timeOfDay: 'both', description: 'Hydrating moisturizer' },
      { step: 4, category: 'protect', timeOfDay: 'morning', description: 'SPF 30+ sunscreen' }
    ],
    recommendations: [
      'Maintain consistent routine for best results',
      'Stay hydrated throughout the day',
      'Get adequate sleep for skin repair'
    ],
    ingredients: ['Hyaluronic Acid', 'Niacinamide', 'Ceramides'],
    avoidIngredients: ['Harsh sulfates', 'Alcohol denat', 'Synthetic fragrances']
  };
}

// Batch analysis
export async function startBatchAnalysis(files: File[]): Promise<{ batchId: string }> {
  // Mock implementation
  return { batchId: `batch_${Date.now()}` };
}

export async function getBatchStatus(batchId: string): Promise<{
  status: 'processing' | 'completed' | 'failed';
  progress: number;
}> {
  // Mock implementation
  return { status: 'completed', progress: 100 };
}

export async function getBatchResults(batchId: string): Promise<{
  results: Array<{ filename: string; report: SkinReport }>;
}> {
  // Mock implementation
  return { results: [] };
}

// Image utilities
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function compressImage(file: File, maxWidth = 1024): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressed = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressed);
          } else {
            reject(new Error('Compression failed'));
          }
        },
        'image/jpeg',
        0.8
      );
    };
    
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = URL.createObjectURL(file);
  });
}

// ============================================
// UTILITY EXPORTS
// ============================================

export const api = {
  // Identity
  getStoredIdentity,
  setStoredIdentity,
  getIdentities,
  
  // Session
  getSession,
  updateSession,
  
  // Chat
  streamChat,
  
  // Reports
  uploadSkinReport,
  parseSkinAnalysisReport,
  
  // Vision
  analyzeWithVision,
  analyzeSkin,
  fileToBase64,
  compressImage,
  
  // Batch
  startBatchAnalysis,
  getBatchStatus,
  getBatchResults,
  
  // Health
  checkHealth,
  
  // Fallbacks
  getFallbackWelcome,
  getFallbackResponse,
  
  // Constants
  API_BASE_URL
};

export default api;
